import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 핫링크한 이미지의 로드 실패를 감지한다.
 *
 * 판정 대상은 인자로 받은 `src` 다 — `src` 가 바뀌면 이전 URL 의 실패 판정은 따라오지
 * 않고 새 URL 을 처음부터 다시 판정한다(같은 `src` 로 되돌아오면 그 URL 에 대한 이전
 * 판정을 재사용한다).
 *
 * `onError` 만으로는 부족하다 — News 페이지는 SSG 라 서버가 `<img>` 를 HTML 에 실어
 * 보내고, 브라우저는 하이드레이션 **전에** 그 이미지를 요청한다. 만료된 CDN URL 처럼
 * 빠르게 실패하는 경우 error 이벤트가 React 가 리스너를 붙이기 전에 발생하고,
 * React 는 하이드레이션 이전 이벤트를 재생하지 않으므로 `onError` 가 끝내 호출되지
 * 않는다. 실제로 404 를 주입해 확인했다: 이미지가 실패했는데도(`naturalWidth === 0`)
 * 폴백이 렌더되지 않았다.
 *
 * 그래서 `<img>` 가 렌더된 커밋에서 DOM 을 한 번 조회해 *이미 실패한* 상태를 잡는다
 * (effect deps 는 `src` 뿐이라 첫 마운트와 `src` 교체 시점에만 돈다) —
 * `complete === true` 이면서 `naturalWidth === 0` 이면 로드가 끝났고 실패한 것이다
 * (로딩 중이면 `complete === false`, 성공이면 `naturalWidth > 0`).
 *
 * @param src `<img>` 에 넣을 URL. 실패 판정을 묶어 두는 식별자다.
 * @returns `imgRef` 를 `<img>` 에 걸고, `onError` 를 그 `onError` 로 넘긴다.
 *   `failed` 가 true 가 되면 호출부가 대체 표시로 교체한다.
 */
export function useImageLoadFailed(src: string) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  // 실패를 "관측한 URL" 을 담는다. boolean 으로 두면 src 가 바뀐 뒤에도 판정이 남아
  // 멀쩡한 새 이미지가 폴백 텍스트로 대체된 채 굳는다.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = failedSrc === src;

  useEffect(() => {
    const img = imgRef.current;
    // `img` 가 null 인 실경로가 있다: *이미 실패로 관측된* src 로 되돌아오면 그 커밋에서
    // failed 가 곧바로 true 라 `<img>` 가 렌더되지 않는데, deps(src)는 바뀌었으므로 이
    // effect 는 돈다(A 실패 → B → A 순서로 실측). 가드를 빼면 그 경로에서
    // `TypeError: Cannot read properties of null (reading 'complete')` 가 난다.
    // 호출부가 imgRef 를 `<img>` 에 걸지 않은 경우도 이 가드가 함께 막는다.
    if (!img) return;
    if (img.complete && img.naturalWidth === 0) setFailedSrc(src);
  }, [src]);

  const onError = useCallback(() => setFailedSrc(src), [src]);

  return { failed, imgRef, onError };
}
